import * as pdfMake from "pdfmake/build/pdfmake.js";

import { ReservationsGetModel } from "@/commons/services/Reservations";
import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 面会予約リマインドのPDF操作
 */
export class RemindReservationsPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "RemindReservationsPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      RemindReservationsPdf: {
        normal: "RemindReservationsPdf.ttf",
        bold: "RemindReservationsPdf.ttf"
      }
    };

    return;
  }

  /**
   * ### 面会予約リマインドPDFを作成してダウンロードさせる
   *
   * @param items 印刷対象の面会予約リマインド
   * @param facilityName 施設名
   * @param fileName ファイル名
   */
  public createAndDownloadPdf(items: Array<ReservationsGetModel>, facilityName: string, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [15, 14, 15, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 7,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "RemindReservationsPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 面会予約リマインド一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   *   - 作成日
   *   - タイトル
   *   - 施設名
   * - コンテンツ部(面会予約リマインド情報)
   */
  private createContent(items: Array<ReservationsGetModel>, facilityName: string): any {
    let contents: Array<any> = [];
    contents = this.createHeader(facilityName);
    contents.push(this.createTable(items));
    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   *
   * @param facilityName 施設名
   */
  private createHeader(facilityName: string): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: "HitomeQコネクト 本日の面会予約一覧",
      style: "title"
    });

    // 施設名、利用者名
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 面会予約リマインド情報
   */
  private createTable(items: Array<ReservationsGetModel>): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        // 各列の幅指定、列数と数が合わないとエラーになるので注意する
        widths: [50, 50, 80, 74, 74, 74, 74]
      }
    };
    const body: Array<any> = [
      [
        { text: "種別", style: "tableHeader" },
        { text: "状態", style: "tableHeader" },
        { text: "面会日時", style: "tableHeader" },
        { text: "面会場所", style: "tableHeader" },
        { text: "利用者名（部屋名）", style: "tableHeader" },
        { text: "面会者名（続柄）", style: "tableHeader" },
        { text: "電話番号", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      const typeName: string = item.type == 0 ? "訪問面会" : "オンライン面会";
      const status: string = item.deleteFlag == true ? "キャンセル済" : item.consented == false ? "未承諾" : "承諾";
      const datetime: string = this.getReservationDatetime(Number(item.datetime));
      body.push([
        { text: typeName, style: { alignment: "center" } },
        { text: status, style: { alignment: "center" } },
        { text: datetime, style: { alignment: "center" } },
        item.location ? item.location : "",
        `${item.residentName}（${item.residentRoom}）`,
        `${item.familyName}（${item.familyRelation}）`,
        item.familyTel
      ]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  private getReservationDatetime(datetime: number): string {
    const dateObject: Date = this.dateUtil.convertNumberToDate(datetime);
    const date: string = this.dateUtil.convertDateToString(dateObject, "mm月dd日");
    const week: string = this.dateUtil.getWeekName(dateObject, "ja");
    const time: string = this.dateUtil.convertDateToString(dateObject, "hh:MM");
    return `${date}（${week}）${time}`;
  }
}
